// OIDC (Auth-Code + PKCE) wiring against the public `brand-mint-console`
// Keycloak client. The access token this yields carries aud=brand-mint +
// resource_access.brand-mint.roles + a non-empty sub, which is exactly what the
// backend's operatorauth gate validates.
//
// The authority is the browser-reachable Keycloak issuer, and the redirect is
// the console origin (identical in `npm run dev` and the nginx container). The
// authority is environment-specific: dev talks to Keycloak on :8081, prod to the
// public issuer. It is injected at image-build time via VITE_KC_AUTHORITY (see
// web/Dockerfile + .github/workflows/images.yml); the fallback below is the local
// dev default, used by `npm run dev` and any build that does not set the arg.
import { UserManager, WebStorageStateStore, type User } from "oidc-client-ts";

// The issuer the browser talks to. This MUST equal the token `iss` the backend
// pins (BRAND_MINT_OIDC_ISSUER). Build prod with
// VITE_KC_AUTHORITY=https://auth.kbsfsdp.de/realms/brand-mint.
const AUTHORITY =
  import.meta.env.VITE_KC_AUTHORITY ?? "http://localhost:8081/realms/brand-mint";
const CLIENT_ID = "brand-mint-console";
// The console is always served from the origin root (:8090 dev + nginx).
const ORIGIN = window.location.origin;

export const userManager = new UserManager({
  authority: AUTHORITY,
  client_id: CLIENT_ID,
  redirect_uri: `${ORIGIN}/`,
  post_logout_redirect_uri: `${ORIGIN}/`,
  response_type: "code",
  scope: "openid profile",
  // PKCE S256 is the default in oidc-client-ts for the code flow; stated
  // explicitly for clarity.
  response_mode: "query",
  // DEV-ONLY token handling. `automaticSilentRenew: false` + localStorage means a
  // long-open session can hold an expired access token (300s TTL) until the next
  // API call 401s (the backend still enforces `exp`) or a reload re-checks
  // `user.expired`. A production build should enable silent renew (hidden-iframe
  // or refresh-token rotation) and prefer sessionStorage/in-memory over
  // localStorage. Fine for the local dev console.
  automaticSilentRenew: false,
  loadUserInfo: false,
  userStore: new WebStorageStateStore({ store: window.localStorage }),
  stateStore: new WebStorageStateStore({ store: window.localStorage }),
});

export type { User };

export function login(): Promise<void> {
  return userManager.signinRedirect();
}

export function logout(): Promise<void> {
  return userManager.signoutRedirect();
}

export async function getUser(): Promise<User | null> {
  return userManager.getUser();
}

// completeLoginIfCallback finishes the redirect leg when Keycloak sends the
// browser back with ?code=...&state=..., then strips those params from the URL
// so a refresh does not replay the exchange. Returns the signed-in User, or the
// already-stored User when this is not a callback, or null when signed out.
export async function completeLoginIfCallback(): Promise<User | null> {
  const params = new URLSearchParams(window.location.search);
  if (params.has("code") && params.has("state")) {
    const user = await userManager.signinRedirectCallback();
    // Clean the URL back to the origin root.
    window.history.replaceState({}, document.title, "/");
    return user;
  }
  return userManager.getUser();
}

// rolesFromUser pulls the brand-mint client roles out of the access token's
// resource_access claim. The UI uses this only for view gating; the API is the
// real enforcement.
export function rolesFromUser(user: User | null): string[] {
  if (!user) return [];
  const claims = decodeJwtPayload(user.access_token);
  const ra = claims?.["resource_access"] as
    | Record<string, { roles?: string[] }>
    | undefined;
  return ra?.["brand-mint"]?.roles ?? [];
}

export function displayName(user: User | null): string {
  if (!user) return "";
  const p = user.profile;
  return (
    (p.preferred_username as string | undefined) ??
    (p.name as string | undefined) ??
    (p.sub as string | undefined) ??
    "operator"
  );
}

// decodeJwtPayload base64url-decodes the JWT payload segment. This is display /
// view-gating only — it does NOT verify the signature (the backend does).
function decodeJwtPayload(jwt: string): Record<string, unknown> | null {
  const parts = jwt.split(".");
  if (parts.length < 2) return null;
  try {
    const b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const pad = b64.length % 4 === 0 ? "" : "=".repeat(4 - (b64.length % 4));
    const json = atob(b64 + pad);
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}
